import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Button from '../components/ui/Button';
import NetworkVisual from '../components/visuals/NetworkVisual';

const ProductReviews = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const includes = [
    {
      title: "Video Reviews",
      desc: "Showcasing product features and usage in high-quality video formats.",
      icon: FiIcons.FiVideo
    },
    {
      title: "Intent-Aligned Content",
      desc: "Product-focused content designed to satisfy exactly what the user is looking for.",
      icon: FiIcons.FiSearch
    },
    {
      title: "Targeted Distribution",
      desc: "Reaching the right audience across relevant platforms and networks.",
      icon: FiIcons.FiShare2
    },
    {
      title: "SEO Integration",
      desc: "Positioning reviews within broader SEO and content strategies for maximum impact.",
      icon: FiIcons.FiTarget
    },
    {
      title: "Long-term Visibility",
      desc: "Campaigns structured to maintain a persistent and authoritative brand presence.",
      icon: FiIcons.FiLayers
    }
  ];

  const benefits = [
    "Increase product awareness",
    "Build trust and credibility",
    "Improve conversion rates",
    "Drive targeted traffic",
    "Support product launches",
    "Enhance brand visibility"
  ];

  return (
    <div className="bg-[#0B0F19] min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-40 pb-24 overflow-hidden border-b border-white/5">
        <NetworkVisual />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight tracking-tight"
            >
              Product Reviews & <br/> <span className="text-yellow-500 text-gradient">Video Review Services</span>
            </motion.h2>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-slate-400 mb-10 leading-relaxed max-w-2xl mx-auto"
            >
              Showcase your product through high-quality reviews and reach targeted audiences across video platforms and search engines.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex justify-center w-full sm:w-auto"
            >
              <Button href="#/contact" variant="primary" className="!from-yellow-600 !to-amber-600 !px-12 !py-5 text-xl font-bold shadow-[0_0_40px_rgba(202,138,4,0.4)] w-full sm:w-auto">
                Start Showcasing Your Product
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Section - Centralized */}
      <section className="py-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          
          {/* Main Trust Header */}
          <div className="max-w-4xl mx-auto text-center mb-24">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-10 tracking-tight">Build Trust with Real Product Reviews</h2>
            <div className="text-slate-400 space-y-8 text-xl leading-relaxed mx-auto">
              <p>
                Product reviews are one of the most effective ways to influence buying decisions. Customers rely on reviews to understand how a product works, how it compares, and whether it fits their needs.
              </p>
              <p>
                PRBoost helps businesses and agencies position their products through structured review content designed to increase visibility, engagement, and trust.
              </p>
            </div>
          </div>

          {/* What This Service Includes */}
          <div className="max-w-6xl mx-auto mb-32">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center tracking-tight">What This Service Includes</h3>
            <div className="flex flex-wrap justify-center gap-6">
              {includes.map((item, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] p-8 rounded-3xl glass-card border-white/5 hover:border-yellow-500/20 transition-all group flex flex-col items-center text-center"
                >
                  <div className="w-16 h-16 rounded-2xl bg-yellow-500/10 flex items-center justify-center text-yellow-400 mb-6 group-hover:scale-110 transition-transform">
                    <SafeIcon icon={item.icon} className="text-3xl" />
                  </div>
                  <h4 className="text-white font-bold text-xl mb-4">{item.title}</h4>
                  <p className="text-slate-400 leading-relaxed text-lg">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Why Product Reviews Matter */}
          <div className="max-w-4xl mx-auto">
            <div className="glass-card rounded-[3rem] p-12 md:p-20 border-yellow-500/20 bg-gradient-to-br from-slate-900/90 to-yellow-900/10 text-center">
              <h3 className="text-3xl md:text-5xl font-bold text-white mb-10 tracking-tight">Why Product Reviews Matter</h3>
              <p className="text-xl md:text-2xl text-slate-400 mb-12 leading-relaxed max-w-2xl mx-auto">
                Consumers often search for reviews before making a purchase. Appearing in these search results can significantly impact your visibility and conversion potential.
              </p>
              <div className="flex justify-center">
                <ul className="space-y-6 text-left inline-flex flex-col">
                  {benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-5 text-slate-300 text-xl">
                      <SafeIcon icon={FiIcons.FiCheckCircle} className="text-yellow-400 text-2xl flex-shrink-0" />
                      <span className="font-medium">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Integration Section */}
      <section className="py-24 bg-slate-900/30 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Combine Reviews with SEO</h2>
            <p className="text-lg text-slate-400 leading-relaxed">
              Product reviews become even more powerful when combined with additional content such as guest posts, brand mentions, and video SEO campaigns.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="p-10 rounded-3xl glass-card border-white/5 text-center flex flex-col items-center">
              <div className="w-16 h-16 rounded-2xl bg-yellow-500/10 flex items-center justify-center text-yellow-400 mb-6">
                <SafeIcon icon={FiIcons.FiTrendingUp} className="text-3xl" />
              </div>
              <h4 className="text-2xl font-bold text-white mb-4">Reinforced Authority</h4>
              <p className="text-slate-400 mb-6 text-lg leading-relaxed">
                PRBoost allows you to position your product across multiple channels, reinforcing visibility and building authority across the web.
              </p>
            </div>
            
            <div className="p-10 rounded-3xl glass-card border-white/5 text-center flex flex-col items-center">
              <div className="w-16 h-16 rounded-2xl bg-yellow-500/10 flex items-center justify-center text-yellow-400 mb-6">
                <SafeIcon icon={FiIcons.FiShield} className="text-3xl" />
              </div>
              <h4 className="text-2xl font-bold text-white mb-4">Consistent Presence</h4>
              <p className="text-slate-400 mb-6 text-lg leading-relaxed">
                This creates a stronger and more consistent presence compared to standalone campaigns, ensuring your brand message is unified.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section className="py-24 bg-[#070b14]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-16 tracking-tight">Who This Service Is For</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              "Brands launching new products",
              "Businesses looking for visibility",
              "Agencies managing marketing",
              "Companies aiming for conversion"
            ].map((persona, i) => (
              <div key={i} className="p-8 rounded-2xl border border-white/5 bg-white/5 hover:border-yellow-500/20 transition-all flex flex-col items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-yellow-500/10 flex items-center justify-center text-yellow-400">
                  <SafeIcon icon={FiIcons.FiCheck} className="text-2xl" />
                </div>
                <span className="text-slate-300 font-bold text-lg">{persona}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-yellow-600/5 blur-[120px] rounded-full"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-extrabold text-white mb-8 tracking-tight">Start Showcasing Your <span className="text-yellow-500 text-gradient">Product</span></h2>
          <p className="text-xl md:text-2xl text-slate-400 mb-12 max-w-3xl mx-auto leading-relaxed">
            Product reviews help potential customers understand your offering and build confidence in your brand. Drive visibility and conversions today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Button href="#/contact" variant="primary" className="!from-yellow-600 !to-amber-600 !px-16 !py-6 text-2xl font-bold shadow-[0_0_50px_rgba(202,138,4,0.3)]">
              Create an Account
            </Button>
            <Button href="#/contact" variant="secondary" className="!px-16 !py-6 text-2xl font-bold">
              Contact Us
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductReviews;
